/**
 * Native platform integration for Capacitor.
 * Provides hooks and utilities for push notifications,
 * status bar, keyboard, and app lifecycle.
 */
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import { PushNotifications } from '@capacitor/push-notifications';
import { SplashScreen } from '@capacitor/splash-screen';
import toast from 'react-hot-toast';
import { shouldSuppressForegroundMessagePush } from '../lib/activeChat';

export const isNativePlatform = Capacitor.isNativePlatform();
export const platform = Capacitor.getPlatform();

export async function initializeNative() {
  if (!isNativePlatform) return;

  document.documentElement.classList.add(`platform-${platform}`);
  // The app shell has a single fixed-width layout; a stray zoom (iOS zooms
  // into focused fields) leaves the page panned with no easy way back.
  document
    .querySelector('meta[name="viewport"]')
    ?.setAttribute(
      'content',
      'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover',
    );

  await SplashScreen.hide();

  try {
    await StatusBar.setOverlaysWebView({ overlay: true });
    await StatusBar.setStyle({ style: Style.Light });
    if (platform === 'android') {
      await StatusBar.setBackgroundColor({ color: '#043370' });
    }
  } catch {
    // Status bar styling is cosmetic; don't block the app if a platform rejects it.
  }

  if (platform === 'ios') {
    await Keyboard.setResizeMode({ mode: KeyboardResize.Native });
    await Keyboard.setScroll({ isDisabled: false });
  }

  Keyboard.addListener('keyboardWillShow', () => {
    document.body.classList.add('keyboard-visible');
  });

  Keyboard.addListener('keyboardWillHide', () => {
    document.body.classList.remove('keyboard-visible');
  });

  App.addListener('appUrlOpen', (data) => {
    try {
      const url = new URL(data.url);
      if (
        url.protocol === 'com.ironforums.app:'
        && (url.hostname === 'linkedin' || url.hostname === 'calendar')
        && url.pathname === '/callback'
      ) {
        return;
      }
      const path = url.pathname;
      if (path && /^\/[\w/-]*$/.test(path)) {
        window.location.hash = path;
      }
    } catch {
      // Invalid URL, ignore
    }
  });
}

export async function registerPushNotifications(
  onTokenReceived: (token: string) => void
) {
  if (!isNativePlatform) return;

  const permission = await PushNotifications.requestPermissions();
  if (permission.receive !== 'granted') return;

  // This runs on every launch and login; drop listeners from earlier calls so
  // a token event doesn't fan out to duplicate handlers.
  await PushNotifications.removeAllListeners();

  // Attach the listener BEFORE register(): when permission is already granted
  // the OS can return the token immediately, and an event fired before the
  // listener exists is silently lost — the device then never reaches the
  // backend until the next full app restart (or ever, if it keeps losing).
  await PushNotifications.addListener('registration', (token) => {
    onTokenReceived(token.value);
  });

  PushNotifications.addListener('pushNotificationReceived', (notification) => {
    if (shouldSuppressForegroundMessagePush(notification.data)) {
      // Already looking at this thread — no banner, no toast.
      void PushNotifications.removeDeliveredNotifications({
        notifications: [notification],
      }).catch(() => {});
      return;
    }
    const title = notification.title?.trim();
    const body = notification.body?.trim();
    if (title || body) {
      toast([title, body].filter(Boolean).join(' — '), { duration: 4000 });
    }
  });

  PushNotifications.addListener(
    'pushNotificationActionPerformed',
    (notification) => {
      const data = notification.notification.data;
      if (data?.url) {
        const url = data.url;
        const isRelativePath = url.startsWith('/') && !url.startsWith('//');
        const isTrustedDomain = /^https:\/\/([\w-]+\.)*ironforums\.(org|app|com)/i.test(url);
        if (isRelativePath || isTrustedDomain) {
          window.location.href = url;
        }
      }
    }
  );

  await PushNotifications.register();
}
